import { useQuery } from '@tanstack/react-query';
import { Outlet } from 'react-router-dom';
import { me, meKeys } from '../api/auth';
import { NotFoundScreen } from '../screens/NotFoundScreen';

/**
 * Phase 7 Task 11: the web-side half of the staff gate. routes.tsx mounts this as the shared
 * parent `element` of the whole `/admin/**` route group (a sibling of `/wardrobe/:companionId`
 * etc., NOT nested inside `<AppShell>` — same "top-level sibling under RequireHandleClaimed"
 * shape that component's own javadoc documents for the wardrobe/battle/minigame full-screen
 * routes, since the admin console is an internal tool with no player tab chrome at all, R6).
 *
 * <p><b>The server remains the real gate.</b> Every `/api/v1/admin/**` call is independently
 * ROLE_STAFF-gated by `auth.SecurityConfig` regardless of what this component decides — this is
 * purely a UX nicety (don't even render the admin shell's own skeleton/nav chrome for a player)
 * on top of that real enforcement, exactly the same "client convenience, server authority" split
 * `flags.ts`'s own `useFlags()` already documents for feature flags.
 *
 * <p><b>No admin-surface oracle (C3).</b> A non-staff account — whether that's an ordinary player
 * or an unauthenticated visitor who somehow lands on `/admin/*` — renders the exact same generic
 * {@link NotFoundScreen} every other unmatched/nonexistent route in this app would show, never a
 * word about staff roles or an admin console existing. `role` is read straight off `/me`
 * (`AccountView.role`, present ONLY for staff — see `api/auth.ts`'s own comment); the `me` query
 * reuses the SAME `['me']` cache `RequireHandleClaimed` already primes one level up the route
 * tree (a fresh admin-route load re-fetches once if stale, same as any other consumer of that key
 * — no separate staff-only fetch exists).
 *
 * <p>Renders nothing (not even a skeleton) while the very first `/me` read is in flight, rather
 * than either the admin shell or the not-found screen — a real player should never see even a
 * one-frame flash of internal-tool chrome, and a real staff account shouldn't see a false
 * not-found flash either. Both settle within one round trip of a query the app has almost always
 * already made by the time a human navigates here by hand.
 */
export function StaffGate() {
  const { data, isPending } = useQuery({ queryKey: meKeys.detail, queryFn: me, retry: false });

  if (isPending) return null;
  if (data?.role !== 'staff') return <NotFoundScreen />;
  return <Outlet />;
}
